/**
 * Error Handling Utilities
 * 
 * Centralized error handling utilities for better error messages and retry logic
 */

/**
 * Extract detailed error information from API errors
 */
export interface ErrorDetails {
  message: string;
  status?: number;
  code?: string;
  field?: string;
  originalError?: any;
}

export const extractErrorDetails = (error: any): ErrorDetails => {
  // Handle Axios errors
  if (error?.response) {
    const response = error.response;
    // Check multiple possible locations for error message
    const errorMessage = 
      response.data?.message || 
      response.data?.error || 
      (Array.isArray(response.data?.message) ? response.data.message.join(' ') : null) ||
      response.statusText || 
      'An unexpected error occurred';
    
    return {
      message: errorMessage,
      status: response.status,
      code: response.data?.code,
      field: response.data?.field,
      originalError: error,
    };
  }
  
  // Handle network errors
  if (error?.message === 'Network Error' || error?.code === 'ECONNABORTED') {
    return {
      message: 'Network error. Please check your internet connection and try again.',
      originalError: error,
    };
  }
  
  // Handle timeout errors
  if (error?.code === 'ETIMEDOUT') {
    return {
      message: 'Request timed out. Please try again.',
      originalError: error,
    };
  }
  
  // Handle generic errors
  return {
    message: error?.message || 'An unexpected error occurred',
    originalError: error,
  };
};

/**
 * Retry function with exponential backoff
 * 
 * @param fn - Function to retry
 * @param maxRetries - Maximum number of retries (default: 3)
 * @param initialDelay - Initial delay in milliseconds (default: 1000)
 * @param shouldRetry - Optional function to determine if error should be retried
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000,
  shouldRetry?: (error: any) => boolean
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;
      
      // Don't retry on last attempt
      if (attempt === maxRetries) {
        break;
      }
      
      // Don't retry on client errors (4xx) unless shouldRetry says otherwise
      const status = error?.response?.status;
      if (status >= 400 && status < 500) {
        // Check if shouldRetry function allows retry
        if (shouldRetry && shouldRetry(error)) {
          // Continue to retry
        } else {
          // Don't retry client errors by default
          throw error;
        }
      }
      
      // Calculate exponential backoff delay
      const delay = initialDelay * Math.pow(2, attempt);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
}

/**
 * Log error with context for debugging
 */
export const logError = (
  context: string,
  error: any,
  additionalInfo?: Record<string, any>
) => {
  const errorDetails = extractErrorDetails(error);
  
  console.error(`[${context}] Error:`, {
    message: errorDetails.message,
    status: errorDetails.status,
    code: errorDetails.code,
    field: errorDetails.field,
    timestamp: new Date().toISOString(),
    ...additionalInfo,
    originalError: error,
  });
};

/**
 * Parse validation errors from backend response
 * Handles concatenated validation messages like "property title should not existproperty description should not exist"
 */
export const parseValidationErrors = (errorMessage: string): string[] => {
  if (!errorMessage || typeof errorMessage !== 'string') {
    return [];
  }
  
  const errors: string[] = [];
  
  // Handle concatenated format: "property title should not existproperty description should not exist..."
  // Split by "property" keyword (case insensitive) to get individual errors
  // The regex uses positive lookahead to split before "property" but keep it in the match
  const parts = errorMessage.split(/(?=property\s+\w+)/i);
  
  parts.forEach(part => {
    const trimmed = part.trim();
    if (!trimmed) return;
    
    // Pattern: "property <field> <message>"
    // Example: "property title should not exist"
    const match = trimmed.match(/^property\s+(\w+)\s+(.+)$/i);
    if (match) {
      const field = match[1];
      const message = match[2].trim();
      errors.push(`${field}: ${message}`);
    } else if (trimmed.startsWith('property')) {
      // Fallback: if it starts with property but doesn't match, try to extract field name
      const fieldMatch = trimmed.match(/^property\s+(\w+)/i);
      if (fieldMatch) {
        const field = fieldMatch[1];
        const message = trimmed.substring(fieldMatch[0].length).trim();
        errors.push(`${field}: ${message || 'validation error'}`);
      }
    }
  });
  
  return errors;
};

/**
 * Format validation errors for display
 */
export const formatValidationErrors = (errors: string[]): string => {
  if (errors.length === 0) {
    return '';
  }
  
  if (errors.length === 1) {
    return errors[0];
  }
  
  // For multiple errors, show first few and count
  if (errors.length <= 3) {
    return errors.join('. ');
  }
  
  // Show first 3 errors and indicate there are more
  const shownErrors = errors.slice(0, 3);
  const remainingCount = errors.length - 3;
  return `${shownErrors.join('. ')}. And ${remainingCount} more validation error${remainingCount > 1 ? 's' : ''}.`;
};

/**
 * Get user-friendly error message
 */
export const getUserFriendlyErrorMessage = (error: any, defaultMessage: string = 'An unexpected error occurred'): string => {
  const errorDetails = extractErrorDetails(error);
  
  // Check if this is a validation error with multiple properties
  if (errorDetails.message && errorDetails.message.includes('property') && errorDetails.message.includes('should not exist')) {
    const validationErrors = parseValidationErrors(errorDetails.message);
    if (validationErrors.length > 0) {
      return formatValidationErrors(validationErrors);
    }
  }
  
  // Check if error response has validation errors array (NestJS format)
  if (error?.response?.data?.message && Array.isArray(error.response.data.message)) {
    const validationErrors = error.response.data.message.map((msg: any) => {
      if (typeof msg === 'string') {
        return msg;
      }
      if (msg.property && msg.constraints) {
        const constraints = Object.values(msg.constraints).join(', ');
        return `${msg.property}: ${constraints}`;
      }
      return JSON.stringify(msg);
    });
    if (validationErrors.length > 0) {
      return formatValidationErrors(validationErrors);
    }
  }
  
  // Return field-specific error if available
  if (errorDetails.field && errorDetails.message) {
    return `${errorDetails.field}: ${errorDetails.message}`;
  }
  
  // Return specific error message
  if (errorDetails.message) {
    return errorDetails.message;
  }
  
  // Return default message
  return defaultMessage;
};

